import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MapPin, AlertTriangle, Wallet, FileText, BarChart3, Download, RefreshCw, Settings, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CoppamAdminTerritory() {
  const [territories, setTerritories] = useState([]);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [territoryDetails, setTerritoryDetails] = useState(null);
  const [fundStatus, setFundStatus] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [fundRatios, setFundRatios] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [territoriesRes, ratiosRes] = await Promise.all([
        axios.get(`${API_URL}/api/territories`),
        axios.get(`${API_URL}/api/funds/ratios`)
      ]);
      
      setTerritories(territoriesRes.data.territories || []);
      setFundRatios(ratiosRes.data);
      
      // Sélectionner le premier territoire par défaut
      if (territoriesRes.data.territories?.length > 0) {
        const firstTerritory = territoriesRes.data.territories[0];
        setSelectedTerritory(firstTerritory.code);
        await loadTerritoryData(firstTerritory.code);
      }
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadTerritoryData = async (code) => {
    try {
      const [detailsRes, fundRes, simsRes] = await Promise.all([
        axios.get(`${API_URL}/api/territories/${code}/parameters`),
        axios.get(`${API_URL}/api/funds/${code}`),
        axios.get(`${API_URL}/api/coppam/simulations?territory_code=${code}&limit=20`)
      ]);
      
      setTerritoryDetails(detailsRes.data);
      setFundStatus(fundRes.data);
      setSimulations(simsRes.data.simulations || []);
    } catch {
    }
  };

  const handleTerritoryChange = async (code) => {
    setSelectedTerritory(code);
    await loadTerritoryData(code);
  };

  const getAlertBadge = (alerte) => {
    if (alerte === 'critique') {
      return <Badge className="bg-red-500 text-white font-semibold">⚠️ Critique</Badge>;
    } else if (alerte === 'attention') {
      return <Badge className="bg-amber-500 text-white font-semibold">⚠️ Attention</Badge>;
    }
    return <Badge className="bg-green-500 text-white font-semibold">✅ Normal</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton />
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Building2 className="h-6 w-6" />
                  Administration Territoire
                </h1>
                <p className="text-blue-200 text-sm">COPPAM Garanties Solidaires - Dashboard Admin</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {fundRatios && (
                <div className="text-right text-sm">
                  <div className="text-blue-200">Couverture globale</div>
                  <div className="text-xl font-bold">{fundRatios.taux_couverture_global.toFixed(1)}%</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Sélecteur de territoire */}
        <div className="flex flex-wrap gap-2 mb-6">
          {territories.map((territory) => (
            <Button
              key={territory.code}
              variant={selectedTerritory === territory.code ? 'default' : 'outline'}
              onClick={() => handleTerritoryChange(territory.code)}
              className={selectedTerritory === territory.code ? 'bg-blue-600' : ''}
            >
              <MapPin className="h-4 w-4 mr-1" />
              {territory.name}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Fonds territorial */}
            {fundStatus && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-purple-600" />
                      Fonds Solidaire FOGEDOM
                    </CardTitle>
                    {getAlertBadge(fundStatus.alerte)}
                  </div>
                  <CardDescription>
                    {fundStatus.territory_name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {(fundStatus.dotation / 1000).toFixed(0)}k €
                      </div>
                      <div className="text-xs text-gray-500">Dotation</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-amber-600">
                        {(fundStatus.engagements / 1000).toFixed(0)}k €
                      </div>
                      <div className="text-xs text-gray-500">Engagements</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {(fundStatus.disponible / 1000).toFixed(0)}k €
                      </div>
                      <div className="text-xs text-gray-500">Disponible</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {fundStatus.taux_couverture.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Couverture</div>
                    </div>
                  </div>

                  {/* Barre de progression */}
                  <div className="mt-4">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          fundStatus.taux_couverture < 30 ? 'bg-red-500' :
                          fundStatus.taux_couverture < 50 ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(fundStatus.taux_couverture, 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Simulations récentes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Simulations récentes
                  </CardTitle>
                  <Badge variant="outline">{simulations.length} simulation(s)</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {simulations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Aucune simulation pour ce territoire</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">ID</th>
                          <th className="text-left py-2">Acteur</th>
                          <th className="text-left py-2">Secteur</th>
                          <th className="text-right py-2">Montant</th>
                          <th className="text-right py-2">Contribution</th>
                          <th className="text-center py-2">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulations.slice(0, 10).map((sim) => (
                          <tr key={sim.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 font-mono text-xs">{sim.id?.slice(0, 15)}...</td>
                            <td className="py-2 capitalize">{sim.type_acteur}</td>
                            <td className="py-2 capitalize">{sim.secteur}</td>
                            <td className="py-2 text-right">{sim.montant_projet?.toLocaleString()} €</td>
                            <td className="py-2 text-right">
                              {sim.contribution?.contribution_mensuelle?.toFixed(2)} €/mois
                            </td>
                            <td className="py-2 text-center">
                              {sim.status === 'eligible' && (
                                <Badge className="bg-green-700 text-white font-semibold">Éligible</Badge>
                              )}
                              {sim.status === 'eligible_avec_conditions' && (
                                <Badge className="bg-amber-700 text-white font-semibold">Conditions</Badge>
                              )}
                              {sim.status === 'non_eligible' && (
                                <Badge className="bg-red-700 text-white font-semibold">Refusé</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Colonne latérale */}
          <div className="space-y-6">
            {/* Paramètres territoire */}
            {territoryDetails && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-gray-600" />
                    Paramètres
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="text-xs text-gray-500">Coefficient CST</span>
                    <div className="text-xl font-bold">{territoryDetails.coefficients.cst}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Coefficient CP-T</span>
                    <div className="text-xl font-bold">{territoryDetails.coefficients.cp_t}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Indice IM-T</span>
                    <div className="text-xl font-bold">{territoryDetails.coefficients.im_t}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Cadre juridique</span>
                    <div className="font-semibold text-sm">{territoryDetails.parameters.civil_code}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Risques majeurs</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {territoryDetails.risks.map((risk, i) => (
                        <Badge key={i} variant="outline" className="text-xs capitalize">
                          {risk}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alertes globales */}
            {fundRatios && (fundRatios.territories_critiques.length > 0 || fundRatios.territories_en_alerte.length > 0) && (
              <Card className="border-amber-300 bg-amber-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5" />
                    Alertes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {fundRatios.territories_critiques.map((code) => (
                    <div key={code} className="flex items-center gap-2 text-red-700">
                      <span className="w-2 h-2 bg-red-500 rounded-full" />
                      {territories.find(t => t.code === code)?.name || code} - Critique
                    </div>
                  ))}
                  {fundRatios.territories_en_alerte.map((code) => (
                    <div key={code} className="flex items-center gap-2 text-amber-700">
                      <span className="w-2 h-2 bg-amber-500 rounded-full" />
                      {territories.find(t => t.code === code)?.name || code} - Attention
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Actions rapides */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <Download className="h-4 w-4 mr-2" />
                  Exporter CSV
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Rapport mensuel
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
